import { useState } from "#imports";

const SEARCH_HISTORY_KEY = "apsh_search_history";
const MAX_SEARCH_HISTORY = 3;

const normalizeSearchQuery = (value: unknown) => {
  if (typeof value !== "string") return "";
  return value.trim().replace(/\s+/g, " ");
};

const parseStoredHistory = (raw: string | null) => {
  if (!raw) return [];

  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];

    const seen = new Set<string>();
    return parsed
      .map((item) => normalizeSearchQuery(item))
      .filter((item): item is string => {
        if (!item) return false;
        const key = item.toLowerCase();
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .slice(0, MAX_SEARCH_HISTORY);
  } catch {
    return [];
  }
};

export const useSearchHistory = () => {
  const history = useState<string[]>("search:history", () => []);

  const persistHistory = () => {
    if (!process.client) return;
    localStorage.setItem(SEARCH_HISTORY_KEY, JSON.stringify(history.value));
  };

  const hydrateSearchHistory = () => {
    if (!process.client) return;
    history.value = parseStoredHistory(
      localStorage.getItem(SEARCH_HISTORY_KEY),
    );
  };

  const addSearchQuery = (value: string) => {
    const query = normalizeSearchQuery(value);
    if (!query) return;

    history.value = [
      query,
      ...history.value.filter(
        (item) => item.toLowerCase() !== query.toLowerCase(),
      ),
    ].slice(0, MAX_SEARCH_HISTORY);

    persistHistory();
  };

  const clearSearchHistory = () => {
    history.value = [];
    if (!process.client) return;
    localStorage.removeItem(SEARCH_HISTORY_KEY);
  };

  return {
    history,
    hydrateSearchHistory,
    addSearchQuery,
    clearSearchHistory,
  };
};
